import React, { Suspense, useEffect, useState, useMemo, useRef } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { Row, Col, Button } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useAppDispatch, RootState } from "./redux-toolkit/store/store";
import { handleUpdatePainLocations } from "./redux-toolkit/slices/painEntryFormSlice";
import { useSelector } from "react-redux";
import { bodyPainColorsByRating } from "./helpers/painEntryFormConstants";

const MaleBody: React.FC<any> = ({
  rotation,
  scale,
  isBodyClickEnable,
  isOrderCreamEntry,
}) => {
  const { isPainLocationsLoading, userPainData } = useSelector(
    (state: RootState) => state.dashboard
  );
  const painEntryForm = useSelector((state: RootState) => state.painEntryForm);
  const orderCreamForm = useSelector((state: RootState) => state.orderCream);

  //import { RootState, AppDispatch } from "./../../redux-toolkit/store/store";
  const dispatch = useAppDispatch();
  const [colors, setColors] = useState<any>(bodyPainColorsByRating);
  const gltf = useLoader(GLTFLoader, "/models/3dbodytwinge.glb");
  const scene = useMemo(() => SkeletonUtils.clone(gltf.scene), [gltf.scene]);
  const [selectedParts, setSelectedParts] = useState(new Set<string>());
  const isUpdatingFromStateRef = useRef(false);

  // Use painEntryForm.painLocations for OrderCream, otherwise use userPainData.painLocations
  // For OrderCream, only use painEntryForm to avoid circular updates
  const painLocationsSource = isOrderCreamEntry
    ? painEntryForm.painLocations || []
    : userPainData.painLocations;

  useEffect(() => {
    // Update selectedParts from Redux state
    const newSelectedParts: Set<string> =
      painLocationsSource && painLocationsSource.length > 0
        ? new Set<string>(painLocationsSource)
        : new Set<string>();

    // Only update if different to avoid unnecessary re-renders
    const currentPartsArray = Array.from(selectedParts).sort();
    const newPartsArray = Array.from(newSelectedParts).sort();

    if (JSON.stringify(currentPartsArray) !== JSON.stringify(newPartsArray)) {
      isUpdatingFromStateRef.current = true;
      setSelectedParts(newSelectedParts);
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromStateRef.current = false;
      }, 0);
    }
  }, [painLocationsSource]);
  useEffect(() => {
    if (scene) {
      // Loop through the parts and apply the color change based on selectedParts
      scene.children.forEach((child) => {
        const partName = child.userData?.name; // Retrieve part name from userData
        child.traverse((node: any) => {
          if (node.isMesh) {
            // Change color based on selection
            if (selectedParts.has(partName)) {
              const rating = userPainData.locationAverages[partName];
              const color = colors[rating] || "red";
              node.material = new THREE.MeshStandardMaterial({
                color: color,
              });
            } else {
              node.material = new THREE.MeshStandardMaterial({
                color: "white", // Default color for unselected parts
              });
            }
          }
        });
      });
    }
  }, [scene, selectedParts]);

  // Handle click event on model parts
  const handleClick = (mesh: any) => {
    if (mesh.isMesh) {
      const partName = mesh.userData?.name; // Retrieve part name

      // Update selected parts when clicked
      setSelectedParts((prevSelected) => {
        const newSelected = new Set(prevSelected);
        if (newSelected.has(partName)) {
          newSelected.delete(partName); // Unselect part if already selected
          mesh.material.color.set("white"); // Revert to default color
        } else {
          newSelected.add(partName); // Select part
          mesh.material.color.set("yellow"); // Highlight selected part
        }
        return newSelected;
      });
    }
  };
  useEffect(() => {
    // Only dispatch if the change came from user interaction, not from Redux state update
    if (isUpdatingFromStateRef.current) {
      return;
    }

    const painLocations = Array.from(selectedParts);
    dispatch(handleUpdatePainLocations(painLocations));
  }, [selectedParts, dispatch]);
  // Apply rotation to the model
  useFrame(() => {
    if (scene) {
      scene.rotation.y = rotation;
    }
  });

  return (
    <primitive
      object={scene}
      scale={scale}
      position={[0, -3, 0]}
      onPointerDown={(e: any) => {
        if (isBodyClickEnable) {
          handleClick(e.object);
        }
      }} // Handle click event
    />
  );
};

const MaleBodyViewer: React.FC<IMaleBodyViewer> = ({
  isBodyClickEnable,
  isOrderCreamEntry = false,
  controlsPosition = "bottom",
}) => {
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(0.3);

  const handleRotationChange = (angle: any) => {
    setRotation(angle);
  };

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 1.5));
  };

  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.1));
  };

  const handleDrag = (event: any) => {
    const circle = event.target;
    const rect = circle.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;
    const angle = Math.atan2(y, x);
    handleRotationChange(angle);
  };

  const dotPosition = {
    left: `${50 + 45 * Math.cos(rotation)}%`,
    top: `${50 + 45 * Math.sin(rotation)}%`,
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: controlsPosition === "right" ? "100%" : "600px",
        margin: "0 auto",
        padding: "20px",
        border: "1px solid #f0f0f0",
        borderRadius: "8px",
        backgroundColor: "#fff",
        textAlign: "center",
        display: "flex",
        flexDirection: controlsPosition === "right" ? "row" : "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ flex: 1, width: "100%" }}>
        <Canvas style={{ height: 400, width: "100%" }}>
          <Suspense fallback={null}>
            <ambientLight />
            <pointLight position={[10, 10, 10]} />
            <MaleBody
              isBodyClickEnable={isBodyClickEnable}
              isOrderCreamEntry={isOrderCreamEntry}
              rotation={rotation}
              scale={[scale, scale, scale]}
            />
            <OrbitControls />
          </Suspense>
        </Canvas>
      </div>
      <div
        style={{
          marginTop: controlsPosition === "bottom" ? "20px" : "0",
          marginLeft: controlsPosition === "right" ? "20px" : "0",
          display: "flex",
          flexDirection: controlsPosition === "right" ? "column" : "row",
          justifyContent: "space-around",
          alignItems: "center",
          width: controlsPosition === "right" ? "auto" : "100%",
          gap: "20px",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100px",
            height: "100px",
            background: "#f0f0f0",
            borderRadius: "50%",
            cursor: "pointer",
            flexShrink: 0,
          }}
          onMouseDown={handleDrag}
          onMouseMove={(e) => e.buttons === 1 && handleDrag(e)}
        >
          <div
            style={{
              position: "absolute",
              width: "10px",
              height: "10px",
              background: "#1890ff",
              borderRadius: "50%",
              transform: "translate(-50%, -50%)",
              ...dotPosition,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button onClick={handleZoomOut} icon={<MinusOutlined />} />
          <Button
            onClick={handleZoomIn}
            icon={<PlusOutlined />}
            style={{ marginLeft: "10px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(MaleBodyViewer);
